import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { client } from 'src/enviroment/api/axios'
import { handleApiError } from 'src/enviroment/api/errorHandler'
import { queryTool } from 'src/enviroment/lib/utils'
import { loadKnowledgeBase } from 'src/enviroment/api/chat/api'
import { tenantRouterKeys } from './tenant'
import { userRouterKeys } from './user'
import { Mutation, Query } from '../index'
import { Project, UpdateProjectData } from 'src/types/projects'
import { GlobalTenantsResponse, Tenant, TenantExclusiveProject, UserTenantRoles } from 'src/types/tenant'
import {
  EditUserResponse,
  LearningMaterialsResponse,
  ProjectsResponse,
  QueryParams,
  TenantsResponse,
  UpdateTenantPayload,
  UsersResponse,
} from 'src/types/superadmin'
import { AddProjectFormData } from 'src/components/organisms/dialogs/AddProjectDialog'
import { CurrentUser } from 'src/types/user'

import { DEFAULT_TAKE } from 'src/enviroment/constants/constants'

export const superAdminRouterKeys = {
  usersList: 'superAdmin.usersList',
  globalProjectList: 'superAdmin.globalProjectList',
  globalTenantsList: 'superAdmin.globalTenantsList',
  tenantsProject: 'superAdmin.tenantsProject',
  currentTenantsOfProject: 'superAdmin.currentTenantsOfProject',
  exclusiveProjectsOfTenant: 'superAdmin.exclusiveProjectsOfTenant',
  editUser: 'superAdmin.editUser',
  learningMaterials: 'superAdmin.learningMaterials',
} as const

export const superAdminRouter = {
  // Users
  users: {
    useInfiniteQuery: (params: QueryParams = {}) => {
      const queryClient = useQueryClient()
      const { take = DEFAULT_TAKE, query, isApproved } = params

      return useInfiniteQuery({
        queryKey: [superAdminRouterKeys.usersList, query, isApproved],
        queryFn: async ({ pageParam }) => {
          const response = await client.get<UsersResponse>('/users', {
            params: {
              cursor: pageParam,
              take,
              query: queryTool(query),
              isApproved,
            },
          })
          return response.data
        },
        enabled: Boolean(queryClient.getQueryData([userRouterKeys.currentUser])),
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      })
    },
  },
  deleteUser: {
    useMutation: (opts?: Mutation<string, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (id: string) => {
          await client.delete(`/users/${id}`)
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries([superAdminRouterKeys.usersList])
          opts?.onSuccess?.(...args)
        },
        ...opts,
      })
    },
  },
  approveUser: {
    useMutation: (opts?: Mutation<string, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (id: string) => {
          await client.patch(`/users/${id}/approve`)
        },
        onSuccess: (...args) => {
          enqueueSnackbar('User approved successfully', { variant: 'success' })
          queryClient.invalidateQueries({
            predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === superAdminRouterKeys.usersList,
          })
          opts?.onSuccess?.(...args)
        },
        ...opts,
      })
    },
  },
  getDetailsForEditUser: {
    useQuery: (userId: string, enabled: boolean, opts?: Query<EditUserResponse>) => {
      return useQuery<EditUserResponse>({
        queryKey: [superAdminRouterKeys.editUser, userId],
        queryFn: async () => {
          const response = await client.get<EditUserResponse>(`/users/${userId}:get-data-for-edit-user-by-superadmin`)
          return response.data
        },
        enabled,
        ...opts,
      })
    },
  },
  editUser: {
    useMutation: (userId: string, opts?: Mutation<EditUserResponse, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (data: EditUserResponse) => {
          await client.patch(`/users/${userId}`, data)
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries({
            predicate: (query) => Array.isArray(query.queryKey) && query.queryKey[0] === superAdminRouterKeys.usersList,
          })
          queryClient.invalidateQueries({ queryKey: [superAdminRouterKeys.editUser, userId] })
          queryClient.invalidateQueries({ queryKey: [superAdminRouterKeys.currentTenantsOfProject] })
          opts?.onSuccess?.(...args)
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },

  // Projects
  globalProjects: {
    useInfiniteQuery: (params: QueryParams = {}) => {
      const queryClient = useQueryClient()
      const { take = DEFAULT_TAKE, query } = params

      return useInfiniteQuery({
        queryKey: [superAdminRouterKeys.globalProjectList, query],
        queryFn: async ({ pageParam }) => {
          const response = await client.get<ProjectsResponse>('/projects', {
            params: {
              cursor: pageParam,
              take,
              query: queryTool(query),
            },
          })
          return response.data
        },
        enabled: Boolean(queryClient.getQueryData([userRouterKeys.currentUser])),
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      })
    },
  },
  addProject: {
    useMutation: (opts?: Mutation<AddProjectFormData, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (data: AddProjectFormData) => {
          await client.post('/projects', data)
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries([superAdminRouterKeys.globalProjectList])
          opts?.onSuccess?.(...args)
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
  updateProject: {
    useMutation: (opts?: Mutation<UpdateProjectData, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (data: UpdateProjectData) => {
          await client.patch(`/projects/${data.id}`, data)
        },
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: [superAdminRouterKeys.globalProjectList] })
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
  refreshThumbnail: {
    useMutation: (opts?: Mutation<string, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (projectId: string) => {
          await client.post(`/projects/${projectId}/thumbnail-refresh`)
        },
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: [superAdminRouterKeys.globalProjectList] })
        },
        onSuccess: (...args) => {
          enqueueSnackbar('Thumbnail refreshed successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
  deleteProject: {
    useMutation: (opts?: Mutation<string, { project: Project; deletedBlobsCount: number }>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (projectId: string) => {
          const res = await client.delete<{ project: Project; deletedBlobsCount: number }>(`/projects/${projectId}`)
          const { project, deletedBlobsCount } = res.data
          if (deletedBlobsCount > 0) {
            const currentUser = queryClient.getQueryData<CurrentUser>([userRouterKeys.currentUser])
            if (currentUser && project.tenantId) {
              loadKnowledgeBase({ userId: currentUser.id, tenantId: project.tenantId, projectId }).catch(() => {})
            }
          }
          return res.data
        },
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: [superAdminRouterKeys.globalProjectList] })
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },

  // Tenants / Workspaces
  globalTenants: {
    useInfiniteQuery: (params: QueryParams = {}) => {
      const queryClient = useQueryClient()
      const { take = DEFAULT_TAKE, query } = params

      return useInfiniteQuery({
        queryKey: [superAdminRouterKeys.globalTenantsList, query],
        queryFn: async ({ pageParam }) => {
          const response = await client.get<TenantsResponse>('/tenants', {
            params: {
              cursor: pageParam,
              take,
              query: queryTool(query),
            },
          })
          return response.data
        },
        enabled: Boolean(queryClient.getQueryData([userRouterKeys.currentUser])),
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      })
    },
  },
  getTenantsByUserId: {
    useQuery: (userId: string, opts?: Query<UserTenantRoles>) => {
      return useQuery<UserTenantRoles>({
        queryKey: ['tenants', userId],
        queryFn: async () => {
          const response = await client.get<UserTenantRoles>(`/users/${userId}:tenant-access`)
          return response.data
        },
        ...opts,
      })
    },
  },
  updateTenant: {
    useMutation: (opts?: Mutation<UpdateTenantPayload, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (data: UpdateTenantPayload) => {
          const formData = new FormData()

          if (data.name) {
            formData.append('name', data.name)
          }

          if (data.plan) {
            formData.append('plan', data.plan)
          }

          if (data.logo instanceof File) {
            formData.append('logo', data.logo)
          } else if (data.logo === null) {
            formData.append('logo', '')
          }

          await client.patch(`/tenants/${data.id}/as-superadmin`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries([superAdminRouterKeys.globalTenantsList])
          queryClient.invalidateQueries([tenantRouterKeys.myTenants])
          opts?.onSuccess?.(...args)
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
  getTenantsByProject: {
    useInfiniteQuery: (params: QueryParams = {}, enabled: boolean) => {
      const { take = DEFAULT_TAKE, query } = params

      return useInfiniteQuery({
        queryKey: [superAdminRouterKeys.tenantsProject, query],
        queryFn: async ({ pageParam }) => {
          const response = await client.get<GlobalTenantsResponse>('/tenants', {
            params: {
              cursor: pageParam,
              take,
              query: queryTool(query),
            },
          })
          return response.data
        },
        enabled,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      })
    },
  },
  getCurrentTenantsOfProject: {
    useQuery: (projectId: string, opts?: Query<Tenant[]>) => {
      return useQuery<Tenant[]>({
        queryKey: [superAdminRouterKeys.currentTenantsOfProject, projectId],
        queryFn: async () => {
          const response = await client.get<Tenant[]>(`/projects/${projectId}:tenants`)
          return response.data
        },
        ...opts,
      })
    },
  },
  getExclusiveProjectsOfTenant: {
    useQuery: (tenantId: string, opts?: Query<TenantExclusiveProject[]>) => {
      return useQuery<TenantExclusiveProject[]>({
        queryKey: [superAdminRouterKeys.exclusiveProjectsOfTenant, tenantId],
        queryFn: async () => {
          const response = await client.get<TenantExclusiveProject[]>(`/tenants/${tenantId}/exclusive-projects`)
          return response.data
        },
        ...opts,
      })
    },
  },
  updateProjectTenants: {
    useMutation: (projectId: string, opts?: Mutation<string[], void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (tenantIds: string[]) => {
          await client.put(`/projects/${projectId}:tenants`, { tenantIds })
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries([superAdminRouterKeys.currentTenantsOfProject, projectId])
          opts?.onSuccess?.(...args)
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
  createWorkspace: {
    useMutation: (opts?: Mutation<{ name: string; userId: string }, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (data: { name: string; userId: string }) => {
          await client.post('/tenants', data)
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries([superAdminRouterKeys.globalTenantsList])
          opts?.onSuccess?.(...args)
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
  deleteTenant: {
    useMutation: (opts?: Mutation<string, { deletedProjects: number; failedProjects: number }>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (tenantId: string) => {
          return (await client.delete<{ deletedProjects: number; failedProjects: number }>(`/tenants/${tenantId}`)).data
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries([superAdminRouterKeys.globalTenantsList])
          opts?.onSuccess?.(...args)
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },

  // Learning Materials
  learningMaterials: {
    useInfiniteQuery: (params: QueryParams = {}) => {
      const queryClient = useQueryClient()
      const { take = 10, query } = params

      return useInfiniteQuery({
        queryKey: [superAdminRouterKeys.learningMaterials, query],
        queryFn: async ({ pageParam }) => {
          const response = await client.get<LearningMaterialsResponse>('/learning-materials', {
            params: {
              cursor: pageParam,
              take,
            },
          })
          return response.data
        },
        enabled: Boolean(queryClient.getQueryData([userRouterKeys.currentUser])),
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      })
    },
  },
  createLearningMaterial: {
    useMutation: (opts?: Mutation<{ name: string; type: string; file: File }, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (data: { name: string; type: string; file: File }) => {
          const formData = new FormData()
          formData.append('name', data.name)
          formData.append('type', data.type)
          formData.append('file', data.file)

          await client.post('/learning-materials:create', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries([superAdminRouterKeys.learningMaterials])
          enqueueSnackbar('Learning material created successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
  deleteLearningMaterial: {
    useMutation: (opts?: Mutation<string, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async (id: string) => {
          await client.delete(`/learning-materials/${id}`)
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries([superAdminRouterKeys.learningMaterials])
          enqueueSnackbar('Learning material deleted successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
  updateLearningMaterialTenants: {
    useMutation: (opts?: Mutation<{ id: string; tenantIds: string[] }, void>) => {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: async ({ id, tenantIds }: { id: string; tenantIds: string[] }) => {
          await client.patch(`/learning-materials/${id}/tenant-access`, { tenantIds })
        },
        onSuccess: (...args) => {
          queryClient.invalidateQueries([superAdminRouterKeys.learningMaterials])
          enqueueSnackbar('Material access updated successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: handleApiError,
        ...opts,
      })
    },
  },
}
